import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BookOpen, ArrowLeft, CheckCircle, AlertTriangle } from "lucide-react";
import axios from "axios";
import DateFilter from "@/components/DateFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function TallyBadge({ tallied }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${
        tallied ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
      }`}
    >
      {tallied ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {tallied ? "Tallied" : "Diff"}
    </span>
  );
}

/* ====== VOUCHER DETAIL VIEW ====== */
function VoucherDetail({ voucherId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/reports/gl-dump`, { params: { voucher_id: voucherId } });
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [voucherId]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }
  if (!data) {
    return <div className="text-center py-8 text-red-600">Voucher not found</div>;
  }

  return (
    <div data-testid="gl-voucher-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="gl-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {data.type} — {data.number || "N/A"}
          </h1>
          <p className="text-sm text-slate-500">
            {formatDate(data.date)} &middot; {data.party || "No party"}
          </p>
        </div>
        <div className="ml-auto">
          <TallyBadge tallied={data.is_tallied} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Debit</p>
            <p className="text-xl font-bold text-blue-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data.total_debit)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Credit</p>
            <p className="text-xl font-bold text-amber-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data.total_credit)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Voucher Amount</p>
            <p className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data.amount)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Ledger lines */}
      <Card className="border border-slate-200 shadow-none mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Accounting Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="voucher-lines-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Ledger Account</TableHead>
                <TableHead className="text-right font-semibold w-40">Debit (&#8377;)</TableHead>
                <TableHead className="text-right font-semibold w-40">Credit (&#8377;)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lines.map((line) => (
                <TableRow key={line.ledger}>
                  <TableCell className="text-sm font-medium">{line.ledger}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-sm text-blue-600">
                    {line.debit > 0 ? formatCurrency(line.debit) : ""}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-sm text-amber-600">
                    {line.credit > 0 ? formatCurrency(line.credit) : ""}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-100 font-bold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right font-mono tabular-nums text-blue-700">
                  {formatCurrency(data.total_debit)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-amber-700">
                  {formatCurrency(data.total_credit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Inventory lines if any */}
      {data.inventory_lines && data.inventory_lines.length > 0 && (
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Stock Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Item</TableHead>
                  <TableHead className="text-right font-semibold w-28">Qty</TableHead>
                  <TableHead className="text-right font-semibold w-28">Rate</TableHead>
                  <TableHead className="text-right font-semibold w-36">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.inventory_lines.map((il) => (
                  <TableRow key={`${il.item}-${il.qty}`}>
                    <TableCell className="text-sm">{il.item}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{il.qty} {il.unit}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(il.rate)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(il.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ====== VOUCHER LIST VIEW ====== */
export default function GLDump() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [untalliedOnly, setUntalliedOnly] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50, ...filters };
      if (searchTerm) params.search = searchTerm;
      if (typeFilter) params.voucher_type = typeFilter;
      if (untalliedOnly) params.untallied_only = true;
      const res = await axios.get(`${API}/reports/gl-dump`, { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, filters, searchTerm, typeFilter, untalliedOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (selectedVoucher) {
    return <VoucherDetail voucherId={selectedVoucher} onBack={() => setSelectedVoucher(null)} />;
  }

  const totalPages = data ? Math.ceil((data.total_count || 0) / (data.page_size || 50)) : 0;

  return (
    <div data-testid="gl-dump">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="gl-title">
          GL Dump — Voucher-wise
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Click any voucher to see its full debit/credit entries
        </p>
      </div>

      <DateFilter onFilterChange={(f) => { setFilters(f); setPage(1); }} />

      {/* Grand totals */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border border-slate-200 shadow-none">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Grand Debit</p>
              <p className="text-lg font-bold text-blue-600 mt-1 font-mono tabular-nums">
                &#8377; {formatCurrency(data.grand_debit)}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-none">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Grand Credit</p>
              <p className="text-lg font-bold text-amber-600 mt-1 font-mono tabular-nums">
                &#8377; {formatCurrency(data.grand_credit)}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Difference</p>
                  <p className="text-lg font-bold text-slate-900 mt-1 font-mono tabular-nums">
                    &#8377; {formatCurrency(data.grand_diff)}
                  </p>
                </div>
                <TallyBadge tallied={Math.abs(data.grand_diff || 0) < 1.01} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search voucher no. or party..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="w-64 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          data-testid="gl-search"
        />
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          data-testid="gl-type-filter"
        >
          <option value="">All Types</option>
          {(data?.voucher_types || []).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => { setUntalliedOnly((v) => !v); setPage(1); }}
          data-testid="gl-untallied-toggle"
          className={`px-3 py-2 rounded-sm text-sm font-medium border transition ${
            untalliedOnly
              ? "bg-red-600 border-red-600 text-white hover:bg-red-700"
              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
          }`}
          title="Show only vouchers where Debit ≠ Credit (data-quality hunt)"
        >
          {untalliedOnly ? "✓ Untallied only" : "Untallied only"}
        </button>
        <span className="text-sm text-slate-500">
          {data?.total_count || 0} {untalliedOnly ? "untallied" : "vouchers"}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="gl-loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Vouchers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-auto">
              <Table data-testid="gl-voucher-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 sticky top-0">
                    <TableHead className="font-semibold w-28">Date</TableHead>
                    <TableHead className="font-semibold w-28">Type</TableHead>
                    <TableHead className="font-semibold w-32">Vch No</TableHead>
                    <TableHead className="font-semibold">Party</TableHead>
                    <TableHead className="text-right font-semibold w-36">Debit (&#8377;)</TableHead>
                    <TableHead className="text-right font-semibold w-36">Credit (&#8377;)</TableHead>
                    <TableHead className="text-center font-semibold w-20">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.vouchers || []).map((v) => (
                    <TableRow
                      key={v.voucher_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelectedVoucher(v.voucher_id)}
                      data-testid={`gl-row-${v.voucher_id}`}
                    >
                      <TableCell className="text-sm">{formatDate(v.date)}</TableCell>
                      <TableCell className="text-sm">
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                          {v.type}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-mono text-blue-700 underline decoration-slate-300 underline-offset-2">
                        {v.number || "-"}
                      </TableCell>
                      <TableCell className="text-sm">{v.party || "-"}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm text-blue-600">
                        {formatCurrency(v.debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm text-amber-600">
                        {formatCurrency(v.credit)}
                      </TableCell>
                      <TableCell className="text-center">
                        <TallyBadge tallied={v.is_tallied} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.vouchers || data.vouchers.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                        No vouchers found. Upload a Daybook XML first.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Prev
          </Button>
          <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
